// Fbm primitive: hash-based value-noise fractal, band-limited per octave against matPixelSize()
// (plan §7 — octaves finer than ~1px are anti-aliased via analytic variance clamping instead of sizzling).
export const FBM_GLSL = `
// Hash-based value noise on a unit lattice (mean ~0.5).  Uses hash01.
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = p - i;
  // MUST bitcast through ivec2 -- a direct negative-float -> uint conversion is UNDEFINED in
  // GLSL ES 300 and drivers clamp it to 0, which collapses the lattice hash to a function of y
  // alone for all x < 0 (see Scatter.glsl's long note: this was the dirt pebble row-lock bug).
  uvec2 u = uvec2(ivec2(i));
  float n00 = hash01(u);
  float n10 = hash01(u + uvec2(1u, 0u));
  float n01 = hash01(u + uvec2(0u, 1u));
  float n11 = hash01(u + uvec2(1u, 1u));
  vec2 s = f * f * (3.0 - 2.0 * f);
  return mix(mix(n00, n10, s.x), mix(n01, n11, s.x), s.y);
}

// Fractal noise over p (world meters).  baseScale = cell size of octave 0 (meters).
// Modes: 0 = fbm, 1 = ridged, 2 = billow, 3 = turbulence (abs-sum) — the turbulence
// addition keeps the vocabulary aligned with TerrainAmbient.fractalType (plan §4.1).
// Per-octave band-limit: analytic variance clamping (octaves whose cell is finer than ~1px
// damp their contrast by cellMeters/px instead of a hard cutoff to zero).
float fbmVal(vec2 p, float baseScale, int octaves, float lacunarity, float persistence, int mode) {
  float px = matPixelSize();
  float amp = 1.0;
  float total = 0.0;
  float norm = 0.0;
  float cellMeters = max(baseScale, 1e-5);
  for (int o = 0; o < 8; o++) {
    if (o >= octaves) break;
    float v = valueNoise(p / cellMeters);
    v = v * 2.0 - 1.0;
    if (mode == 1) v = 1.0 - abs(v);
    else if (mode == 2) v = -abs(v);
    else if (mode == 3) v = abs(v);
    float mean = 0.0;
    if (mode == 1 || mode == 3) mean = 0.5;
    else if (mode == 2) mean = -0.5;

    // Per-octave band-limit — analytic variance clamping (damping contrast instead of hard
    // cutoffs).  A pixel of width px integrates ~(px/cellMeters)² lattice cells of a sub-pixel
    // octave; box-averaging N uncorrelated value-noise cells scales the resolved amplitude by
    // 1/sqrt(N) = cellMeters/px (2D).  So the correct anti-aliased octave keeps FULL contrast at
    // cell >= ~1px and damps its contrast proportionally to cellMeters/px below that — approaching
    // the mean only as cell -> 0.  Unlike the old smoothstep(1px, 2px, cellMeters) hard cutoff
    // (which zeroed every sub-pixel octave and made the result dull/flat — why "sharpness" was
    // preferred), this keeps the true AA variance: no more shimmer than a supersampled render,
    // just without the over-flattened look.
    float fade = clamp(cellMeters / max(px, 1e-5), 0.0, 1.0);
    v = mix(mean, v, fade);

    total += amp * v;
    norm += amp;
    amp *= persistence;
    cellMeters /= max(lacunarity, 1.0001);
  }
  return norm > 0.0 ? total / norm : 0.0;
}

// Set11 Phase 1 — analytic derivatives.  Same hash + same smoothstep as valueNoise, so the
// value channel is bit-identical to valueNoise(p) (the operation order matches exactly).
// Returns (value, d/dx, d/dy) in LATTICE units (the caller divides by cellMeters to get
// per-metre; fbmValD does that via the chain rule).
vec3 valueNoiseD(vec2 p) {
  vec2 i = floor(p);
  vec2 f = p - i;
  // MUST bitcast through ivec2 (negative-coord rule — see valueNoise above).
  uvec2 u = uvec2(ivec2(i));
  float n00 = hash01(u);
  float n10 = hash01(u + uvec2(1u, 0u));
  float n01 = hash01(u + uvec2(0u, 1u));
  float n11 = hash01(u + uvec2(1u, 1u));
  vec2 s  = f * f * (3.0 - 2.0 * f);    // smoothstep
  vec2 ds = 6.0 * f * (1.0 - f);        // its derivative
  float a = n00, b = n10 - n00, c = n01 - n00, d = n11 - n10 - n01 + n00;
  float v  = a + b * s.x + c * s.y + d * s.x * s.y;
  float vx = (b + d * s.y) * ds.x;
  float vy = (c + d * s.x) * ds.y;
  return vec3(v, vx, vy);
}

// Mirrors fbmVal EXACTLY (same 8-iteration bound, same mode remaps, same per-octave
// band-limit mix, same /norm) and additionally returns the world-space gradient.  Returns
// (value, dv/dx, dv/dz) per METRE.  Set11 Phase 1.
vec3 fbmValD(vec2 p, float baseScale, int octaves, float lacunarity, float persistence, int mode) {
  float px = matPixelSize();
  float amp = 1.0;
  float total = 0.0;
  float norm = 0.0;
  vec2 grad = vec2(0.0);
  float cellMeters = max(baseScale, 1e-5);
  for (int o = 0; o < 8; o++) {
    if (o >= octaves) break;
    vec3 nd = valueNoiseD(p / cellMeters);
    float v = nd.x * 2.0 - 1.0;           // matches fbmVal's remap
    vec2 dv = nd.yz * 2.0 / cellMeters;   // chain rule: d/dp of (2n-1) with p scaled by cellMeters
    // Mode remaps, differentiated.  abs(v) has derivative sign(v)*dv; the kink at v == 0 is a
    // measure-zero set (turbulence creases are a real feature of the mode, not an artifact).
    if (mode == 1) { dv = -sign(v) * dv; v = 1.0 - abs(v); }
    else if (mode == 2) { dv = -sign(v) * dv; v = -abs(v); }
    else if (mode == 3) { dv =  sign(v) * dv; v =  abs(v); }

    // Per-octave band-limit — mirrors fbmVal EXACTLY (same mean, same clamp fade) so the value
    // channel stays bit-identical to fbmVal.  mean and fade are p-independent (functions of
    // cellMeters/matPixelSize only), so d(mix(mean, v, fade))/dp = fade * dvRaw — the gradient
    // scales with the damped contrast, so value and gradient stay consistent (no invented
    // sub-pixel relief: at very fine octaves the contrast -> 0 and so does the gradient).
    float mean = 0.0;
    if (mode == 1 || mode == 3) mean = 0.5;
    else if (mode == 2) mean = -0.5;
    float fade = clamp(cellMeters / max(px, 1e-5), 0.0, 1.0);
    v = mix(mean, v, fade);
    dv *= fade;

    total += amp * v;
    grad  += amp * dv;
    norm  += amp;
    amp *= persistence;
    cellMeters /= max(lacunarity, 1.0001);
  }
  float inv = norm > 0.0 ? 1.0 / norm : 0.0;
  return vec3(total * inv, grad * inv);
}
`;